import { isLocalEAPDevMode } from '@canvas/frame-api-devloop/is-dev-mode';
import {
  type RawContentAPI,
  type ContentAPI,
  type RawWhiteboardSessionData,
  type FrameToken,
  type RawFrameToken,
} from '@canvas/frame-api-types';
import { type BoardId } from '@canvas/model/board';
import { validateEntitlementAndTransformToPaywallState } from '@canvas/paywall';

import { validatePoliciesAndTransformToDataSecurityPolicy } from './policies';

export const isFrameAPI = (maybeAPI: any): maybeAPI is RawContentAPI => {
  return (
    maybeAPI &&
    typeof maybeAPI.goToContent === 'function' &&
    typeof maybeAPI.goToHome === 'function' &&
    typeof maybeAPI.goToContainer === 'function' &&
    typeof maybeAPI.onTokenInit === 'function' &&
    typeof maybeAPI.onTokenChange === 'function' &&
    typeof maybeAPI.onContainerInit === 'function' &&
    typeof maybeAPI.onContainerChange === 'function' &&
    typeof maybeAPI.preloadToken === 'function' &&
    typeof maybeAPI.getToken === 'function' &&
    typeof maybeAPI.sessionData === 'object'
  );
};

export function isLocalEAPTestMode() {
  return process.env.MODE === 'test' || process.env.MODE === 'online';
}

export function isAuthlessOnlineMode() {
  if (!globalThis.location) {
    return false;
  }

  try {
    const url = new URL(globalThis.location.href);
    return url.pathname.match(/\/online\/[a-zA-Z0-9-]+$/);
  } catch {
    return false;
  }
}

export function isAuthlessWebviewMode() {
  return (
    isAuthlessOnlineMode() &&
    new URL(globalThis.location.href).searchParams.has('webview')
  );
}

export const transformToken = (
  token: RawFrameToken | undefined
): FrameToken | undefined =>
  token !== undefined
    ? {
        ...token,
        features: validateEntitlementAndTransformToPaywallState(token.features),
        editionOnlyUseForAnalytics: token.edition,
        dataSecurityPolicy: validatePoliciesAndTransformToDataSecurityPolicy(
          token.policies
        ),
      }
    : undefined;

/**
 * @privateRemarks We've kept transformations of the token in Confluence
 * Frontend (CFE), particularly for
 * feature-gating, to a minimum so as to avoid as many changes needed in CFE
 * as possible.
 *
 * However, at the same time, we want to want to transform feature data into
 * something meaningful to our app as soon as the data enters into the Canvas app.
 *
 * As such, we wrap the content API to perform these transformations, keeping
 * the transformation code to a single point in our codebase
 */

export const checkUrlFeatureFlagOverride = (key: string, href: string) => {
  const url = new URL(href);

  // integration tests use a query parameter to override isFeatureActive flags via darkFeatures option
  for (const [queryParamKey, queryParamValue] of url.searchParams.entries()) {
    // ff key is used to hold multiple feature flag override keys
    if (queryParamKey === 'ff') {
      if (queryParamValue === key) {
        return true;
      }
      if (queryParamValue === `disable__${key}`) {
        return false;
      }
    }
  }
};

export class CanvasContentAPI implements ContentAPI {
  constructor(private readonly rawContentAPI: RawContentAPI) {}

  get sessionData(): RawWhiteboardSessionData {
    return this.rawContentAPI.sessionData;
  }

  goToContent = this.rawContentAPI.goToContent.bind(this.rawContentAPI);
  goToContainer = this.rawContentAPI.goToContainer.bind(this.rawContentAPI);
  goToHome = this.rawContentAPI.goToHome.bind(this.rawContentAPI);
  onTokenInit(handler: (context: FrameToken | undefined) => void) {
    this.rawContentAPI.onTokenInit((rawContext) => {
      handler(transformToken(rawContext));
    });
  }

  onTokenChange(handler: (context: FrameToken | undefined) => void) {
    this.rawContentAPI.onTokenChange((rawContext) => {
      handler(transformToken(rawContext));
    });
  }
  preloadToken = this.rawContentAPI.preloadToken.bind(this.rawContentAPI);
  preloadContent = this.rawContentAPI.preloadContent?.bind(this.rawContentAPI);

  async getToken(id: BoardId | undefined) {
    const token = await this.rawContentAPI.getToken(id);

    return transformToken(token);
  }
  refreshToken = this.rawContentAPI.refreshToken?.bind(this.rawContentAPI);

  onContainerInit = this.rawContentAPI.onContainerInit.bind(this.rawContentAPI);
  onContainerChange = this.rawContentAPI.onContainerChange.bind(this.rawContentAPI);

  onRouteChange = this.rawContentAPI.onRouteChange?.bind(this.rawContentAPI);

  onContentLoadSucceeded = this.rawContentAPI.onContentLoadSucceeded?.bind(
    this.rawContentAPI
  );
  onContentLoadFailed = this.rawContentAPI.onContentLoadFailed?.bind(this.rawContentAPI);
  onContentLoadAborted = this.rawContentAPI.onContentLoadAborted?.bind(
    this.rawContentAPI
  );

  setNavigationLockoutState = this.rawContentAPI.setNavigationLockoutState?.bind(
    this.rawContentAPI
  );

  isFeatureActive = (key: string) => {
    try {
      try {
        if (typeof window !== 'undefined') {
          const urlFlagOverride = checkUrlFeatureFlagOverride(
            key,
            // eslint-disable-next-line no-restricted-syntax
            isLocalEAPDevMode() ? window.location.href : window?.top?.location.href ?? ''
          );

          if (urlFlagOverride !== undefined) {
            return urlFlagOverride;
          }
        }
      } catch (error) {
        console.error('checkUrlFeatureFlagOverride error:', error);
      }

      return this.rawContentAPI.isFeatureActive?.(key) ?? false;
    } catch (error) {
      console.error('isFeatureActive error:', error);
      return false;
    }
  };
  platformFeatureFlagResolver = this.rawContentAPI.platformFeatureFlagResolver?.bind(
    this.rawContentAPI
  );

  updateCollaborators = this.rawContentAPI.updateCollaborators?.bind(this.rawContentAPI);
  updateMainActions = this.rawContentAPI.updateMainActions?.bind(this.rawContentAPI);
  updateMoreActions = this.rawContentAPI.updateMoreActions?.bind(this.rawContentAPI);
  updateBoardStatus = this.rawContentAPI.updateBoardStatus?.bind(this.rawContentAPI);

  registerStartOnboardingCallback =
    this.rawContentAPI.registerStartOnboardingCallback?.bind(this.rawContentAPI);
  onOnboardingChange = this.rawContentAPI.onOnboardingChange?.bind(this.rawContentAPI);
  onOnboardingEnd = this.rawContentAPI.onOnboardingEnd?.bind(this.rawContentAPI);

  onOrgIdChange = this.rawContentAPI.onOrgIdChange?.bind(this.rawContentAPI);

  getOrgId = this.rawContentAPI.getOrgId?.bind(this.rawContentAPI);

  showEditionUpgradeCTA = this.rawContentAPI.showEditionUpgradeCTA?.bind(
    this.rawContentAPI
  );

  closeEditionUpgradeCTA = this.rawContentAPI.closeEditionUpgradeCTA?.bind(
    this.rawContentAPI
  );

  showFeedbackCollectorModal = this.rawContentAPI.showFeedbackCollectorModal?.bind(
    this.rawContentAPI
  );

  closeFeedbackCollectorModal = this.rawContentAPI.closeFeedbackCollectorModal?.bind(
    this.rawContentAPI
  );

  onThemeChange = this.rawContentAPI.onThemeChange?.bind(this.rawContentAPI);

  onThemeLoaded = this.rawContentAPI.onThemeLoaded?.bind(this.rawContentAPI);

  onOnboardingReady = this.rawContentAPI.onOnboardingReady?.bind(this.rawContentAPI);

  triggerIntegration = this.rawContentAPI.triggerIntegration?.bind(this.rawContentAPI);
  checkIntegration = this.rawContentAPI.checkIntegration?.bind(this.rawContentAPI);
  showWhiteboardInternalModal = this.rawContentAPI.showWhiteboardInternalModal?.bind(
    this.rawContentAPI
  );

  closeWhiteboardInternalModal = this.rawContentAPI.closeWhiteboardInternalModal?.bind(
    this.rawContentAPI
  );

  registerDocumentTransformerCallback =
    this.rawContentAPI.registerDocumentTransformerCallback?.bind(this.rawContentAPI);

  registerEventHandler = this.rawContentAPI.registerEventHandler?.bind(
    this.rawContentAPI
  );

  unregisterEventHandler = this.rawContentAPI.unregisterEventHandler?.bind(
    this.rawContentAPI
  );

  triggerEvent = this.rawContentAPI.triggerEvent?.bind(this.rawContentAPI);

  getVersionName = this.rawContentAPI.getVersionName?.bind(this.rawContentAPI);

  isExternalShareRequest = this.rawContentAPI.isExternalShareRequest;

  getPublicLink = this.rawContentAPI.getPublicLink?.bind(this.rawContentAPI);
  refetchPublicLink = this.rawContentAPI.refetchPublicLink?.bind(this.rawContentAPI);

  getTitle = this.rawContentAPI.getTitle?.bind(this.rawContentAPI);

  getContentCreatorId = this.rawContentAPI.getContentCreatorId?.bind(this.rawContentAPI);

  /**
   * AI Agent bridge definitions
   */
  registerAIAgentToggleHandler = this.rawContentAPI.registerAIAgentToggleHandler?.bind(
    this.rawContentAPI
  );
  toggleAIAgentDialog = this.rawContentAPI.toggleAIAgentDialog?.bind(this.rawContentAPI);
  toggleAIAgentRightPanel = this.rawContentAPI.toggleAIAgentRightPanel?.bind(
    this.rawContentAPI
  );
  updateSmartActions = this.rawContentAPI.updateSmartActions?.bind(this.rawContentAPI);
  updateAIAgentDialogState = this.rawContentAPI.updateAIAgentDialogState?.bind(
    this.rawContentAPI
  );
  getAIAgentDialogMountPoint = this.rawContentAPI.getAIAgentDialogMountPoint?.bind(
    this.rawContentAPI
  );
  onConfluenceSidebarStateChange =
    this.rawContentAPI.onConfluenceSidebarStateChange?.bind(this.rawContentAPI);

  confluenceAPI = {
    sidebar: {
      showCommentsPanel:
        this.rawContentAPI.confluenceAPI?.sidebar?.showCommentsPanel?.bind(
          this.rawContentAPI.confluenceAPI?.sidebar
        ),
      hideCommentsPanel:
        this.rawContentAPI.confluenceAPI?.sidebar?.hideCommentsPanel?.bind(
          this.rawContentAPI.confluenceAPI?.sidebar
        ),
      onConfluenceSidebarStateChange:
        this.rawContentAPI.confluenceAPI?.sidebar?.onConfluenceSidebarStateChange?.bind(
          this.rawContentAPI.confluenceAPI?.sidebar
        ),
      showDetailsPanel: this.rawContentAPI.confluenceAPI?.sidebar?.showDetailsPanel?.bind(
        this.rawContentAPI.confluenceAPI?.sidebar
      ),
      hideDetailsPanel: this.rawContentAPI.confluenceAPI?.sidebar?.hideDetailsPanel?.bind(
        this.rawContentAPI.confluenceAPI?.sidebar
      ),
    },
    sideNav: {
      toggleSideNav: this.rawContentAPI.confluenceAPI?.sideNav?.toggleSideNav?.bind(
        this.rawContentAPI.confluenceAPI?.sideNav
      ),
      expandSideNav: this.rawContentAPI.confluenceAPI?.sideNav?.expandSideNav?.bind(
        this.rawContentAPI.confluenceAPI?.sideNav
      ),
      collapseSideNav: this.rawContentAPI.confluenceAPI?.sideNav?.collapseSideNav?.bind(
        this.rawContentAPI.confluenceAPI?.sideNav
      ),
      getIsSideNavCollapsed:
        this.rawContentAPI.confluenceAPI?.sideNav?.getIsSideNavCollapsed?.bind(
          this.rawContentAPI.confluenceAPI?.sideNav
        ),
    },
    comments: {
      onCommentsPanelSelectThread:
        this.rawContentAPI.confluenceAPI?.comments?.onCommentsPanelSelectThread?.bind(
          this.rawContentAPI.confluenceAPI?.comments
        ),
      registerOnReopenResolvedComment:
        this.rawContentAPI.confluenceAPI?.comments?.registerOnReopenResolvedComment?.bind(
          this.rawContentAPI.confluenceAPI?.comments
        ),
      selectCommentThread:
        this.rawContentAPI.confluenceAPI?.comments?.selectCommentThread?.bind(
          this.rawContentAPI.confluenceAPI?.comments
        ),
      registerOnDeleteComment:
        this.rawContentAPI.confluenceAPI?.comments?.registerOnDeleteComment?.bind(
          this.rawContentAPI.confluenceAPI?.comments
        ),
      registerOnUpdateComment:
        this.rawContentAPI.confluenceAPI?.comments?.registerOnUpdateComment?.bind(
          this.rawContentAPI.confluenceAPI?.comments
        ),
      registerOnResolveComment:
        this.rawContentAPI.confluenceAPI?.comments?.registerOnResolveComment?.bind(
          this.rawContentAPI.confluenceAPI?.comments
        ),
      triggerCommentPanelRefresh:
        this.rawContentAPI.confluenceAPI?.comments?.triggerCommentPanelRefresh?.bind(
          this.rawContentAPI.confluenceAPI?.comments
        ),
      registerOnUpdateReaction:
        this.rawContentAPI.confluenceAPI?.comments?.registerOnUpdateReaction?.bind(
          this.rawContentAPI.confluenceAPI?.comments
        ),
    },
    modals: {
      openBulkImportModalToSpecificSource:
        this.rawContentAPI.confluenceAPI?.modals?.openBulkImportModalToSpecificSource?.bind(
          this.rawContentAPI.confluenceAPI?.modals
        ),
      sendBulkImportFeedbackPositive:
        this.rawContentAPI.confluenceAPI?.modals?.sendBulkImportFeedbackPositive?.bind(
          this.rawContentAPI.confluenceAPI?.modals
        ),
      getIsBulkImportFeedbackEligible:
        this.rawContentAPI.confluenceAPI?.modals?.getIsBulkImportFeedbackEligible?.bind(
          this.rawContentAPI.confluenceAPI?.modals
        ),
      setBulkImportFeedbackIneligible:
        this.rawContentAPI.confluenceAPI?.modals?.setBulkImportFeedbackIneligible?.bind(
          this.rawContentAPI.confluenceAPI?.modals
        ),
      showBulkImportFeedbackModal:
        this.rawContentAPI.confluenceAPI?.modals?.showBulkImportFeedbackModal?.bind(
          this.rawContentAPI.confluenceAPI?.modals
        ),
      registerOnBulkImportFeedbackModalSubmitted:
        this.rawContentAPI.confluenceAPI?.modals?.registerOnBulkImportFeedbackModalSubmitted?.bind(
          this.rawContentAPI.confluenceAPI?.modals
        ),
      showFlag: this.rawContentAPI.confluenceAPI?.modals?.showFlag?.bind(
        this.rawContentAPI.confluenceAPI?.modals
      ),
      hideFlag: this.rawContentAPI.confluenceAPI?.modals?.hideFlag?.bind(
        this.rawContentAPI.confluenceAPI?.modals
      ),
    },
    shell: {
      updateCollaborators:
        this.rawContentAPI.confluenceAPI?.shell?.updateCollaborators?.bind(
          this.rawContentAPI.confluenceAPI?.shell
        ),
      updateMainActions: this.rawContentAPI.confluenceAPI?.shell?.updateMainActions?.bind(
        this.rawContentAPI.confluenceAPI?.shell
      ),
      updateBoardStatus: this.rawContentAPI.confluenceAPI?.shell?.updateBoardStatus?.bind(
        this.rawContentAPI.confluenceAPI?.shell
      ),
      updateMoreActions: this.rawContentAPI.confluenceAPI?.shell?.updateMoreActions?.bind(
        this.rawContentAPI.confluenceAPI?.shell
      ),
      updateTitle: this.rawContentAPI.confluenceAPI?.shell?.updateTitle?.bind(
        this.rawContentAPI.confluenceAPI?.shell
      ),
      isFeatureActive: this.rawContentAPI.confluenceAPI?.shell?.isFeatureActive?.bind(
        this.rawContentAPI.confluenceAPI?.shell
      ),
    },
  };
}
